import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { hydraCollectionToArray, replaceStringParams } from 'app/helpers';
import { StorageService } from 'app/core';
import { addQueryParams } from '../../../helpers/functions';

const CARRIERS_LIST = `${environment.apiUrl}/public/carriers`;
const TRACKING_PAGE_NUMBERS = `${environment.apiUrl}/public/tracking_page_links/:hash/shipments`;
const TRACKING_PAGE_NUMBER_CHECKPOINTS = `${environment.apiUrl}/public/tracking_page_links/:hash/shipments/:id/checkpoints`;
const UPDATE_REVIEW = `${environment.apiUrl}/public/tracking_page_links/:hash/reviews`;
const SIDEBAR_ITEMS = `${environment.apiUrl}/public/tracking_page_links/:hash/sidebar_items`;

const CACHING_KEY = btoa('carrier');

@Injectable()
export class TrackingPageNumbersDataSerivce {
  constructor(
    private http: HttpClient,
    private storageService: StorageService
  ) {}

  static carriers: any[];

  static setCarriers(carriers) {
    this.carriers = carriers;
  }

  getDomain() {
    const selectedDomain = this.storageService.getItem('selectedDomain');
    if (!!selectedDomain) {
      return selectedDomain;
    }
    const isDomainOnly = window.location.hostname.split('.').length === 2;
    if (isDomainOnly) {
      return window.location.hostname;
    }
    const [subdomain, ...domain] = window.location.hostname.split('.');
    return this.isCustomDomain(domain.join('.'))
      ? window.location.hostname
      : subdomain;
  }

  getLocale() {
    return this.storageService.getItem('selectedLocale');
  }

  isCustomDomain(domain: string): boolean {
    return !environment.appUrl.includes(domain);
  }

  addDomainParams() {
    return {
      params: addQueryParams({
        domain: this.getDomain(),
        _locale: this.getLocale()
      })
    };
  }

  fetchCarriers() {
    return this.isCacheValid()
      ? of(this.getCachedResults())
      : this.http
          .get(CARRIERS_LIST)
          .pipe(map(hydraCollectionToArray), tap(this.cacheResults()));
  }

  isCacheValid() {
    const { appVersion } = this.storageService.getItem(CACHING_KEY) || {
      appVersion: null
    };
    return appVersion === environment.appVersion;
  }

  getCachedResults() {
    const { items } = this.storageService.getItem(CACHING_KEY) || {
      items: []
    };
    return items;
  }

  cacheResults() {
    return (items) => {
      this.storageService.setItem(CACHING_KEY, {
        items,
        appVersion: environment.appVersion
      });
    };
  }

  fetchCheckpoints({ id, hash }) {
    return this.http
      .get<{ items: any[]; totalItems: number }>(
        replaceStringParams(TRACKING_PAGE_NUMBER_CHECKPOINTS, { id, hash }),
        this.addDomainParams()
      )
      .pipe(map(({ items }) => ({ id, checkpoints: items })));
  }

  fetchSidebarNumbers(sidebarItemId, hash) {
    const params = addQueryParams(
      sidebarItemId === null ? { sidebarItemIdNull: true } : { sidebarItemId },
      this.addDomainParams().params
    );
    return this.http.get<any>(
      replaceStringParams(TRACKING_PAGE_NUMBERS, { hash }),
      {
        params
      }
    );
  }

  updateReview(review, hash) {
    return this.http.post(
      replaceStringParams(UPDATE_REVIEW, { hash }),
      { ...review, domain: this.getDomain() },
      this.addDomainParams()
    );
  }

  loadSidebarItems(hash, queryParams = {}) {
    // TODO: Fix this with pagination
    const params = addQueryParams(queryParams, this.addDomainParams().params);
    return this.http.get(replaceStringParams(SIDEBAR_ITEMS, { hash }), {
      params
    });
  }

  loadMoreTrackingNumbers(hash, sidebarItemId, itemsPerPage) {
    const params = addQueryParams(
      sidebarItemId === null
        ? { sidebarItemIdNull: true, itemsPerPage }
        : { sidebarItemId, itemsPerPage },
      this.addDomainParams().params
    );
    return this.http.get(replaceStringParams(TRACKING_PAGE_NUMBERS, { hash }), {
      params
    });
  }
}
